import realtimeService from './realtimeService';

/**
 * Service to handle query progress tracking and user input requirements
 * @azure This follows Azure best practices for event-driven asynchronous communication
 * with proper error handling and resource cleanup
 */
class QueryProgressService {
  constructor() {
    this.progressListeners = new Map();
    this.sessionProgress = new Map();
    this.initialized = false;
    this.activeSession = null;
  }
  
  /**
   * Initialize the service and set up event listeners
   * @returns {Promise<void>}
   * @azure Follows Azure best practice of centralized event registration
   */
  async initialize() {
    if (this.initialized) return;
    
    try {
      // Set up listener for query progress events
      this.cleanup = realtimeService.registerEventHandlers({
        'query_progress_update': this.handleProgressEvent.bind(this)
      });
      
      this.initialized = true;
      console.log('QueryProgressService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize QueryProgressService:', error);
      // Retry initialization after a delay
      setTimeout(() => {
        if (!this.initialized) {
          this.initialize();
        }
      }, 5000);
    }
  }
  
  /**
   * Handle incoming progress events
   * @param {Object} event The progress event
   * @azure Follows Azure best practice for telemetry and event processing
   */
  handleProgressEvent(event) {
    if (!event || !event.data) {
      console.warn('Invalid query progress event received');
      return;
    }
    
    try {
      const { sessionId } = event.data;
      if (!sessionId) {
        console.warn('Query progress event missing sessionId:', event);
        return;
      }
      
      const progressData = event.data;
      
      // Enhance progress data with metadata
      const enhancedData = this._enhanceProgressData(progressData);
      
      // Store progress for this session
      this._updateSessionProgress(sessionId, enhancedData);
      
      // Notify all listeners for this session
      this._notifyListeners(sessionId, enhancedData);
      
      // Also notify global listeners
      this._notifyListeners('global', enhancedData);
    } catch (error) {
      console.error('Error processing progress event:', error, event);
    }
  }
  
  /**
   * Enhance the progress data with additional metadata
   * @param {Object} data The original progress data
   * @returns {Object} Enhanced progress data
   * @private
   */
  _enhanceProgressData(data) {
    if (!data) return data;
    
    // Deep clone to avoid mutations
    const enhanced = JSON.parse(JSON.stringify(data));
    
    // Add computed properties
    enhanced.isComplexQuery = this._isComplexQuery(enhanced);
    enhanced.requiresUserAction = enhanced.stage === 'waiting_for_user_input';
    
    // Add timestamp if missing
    if (!enhanced.timestamp) {
      enhanced.timestamp = new Date().toISOString();
    }
    
    // Track query complexity over time
    if (enhanced.sessionId && this.sessionProgress.has(enhanced.sessionId)) {
      const prevProgress = this.getLatestProgress(enhanced.sessionId);
      if (prevProgress && prevProgress.isComplexQuery) {
        enhanced.isComplexQuery = true;
      }
    }
    
    return enhanced;
  }
  
  /**
   * Determine if a query is complex based on the progress data
   * @param {Object} progressData Progress data
   * @returns {boolean} True if the query is complex
   * @private
   */
  _isComplexQuery(progressData) {
    if (!progressData) return false;
    
    return (
      progressData.stage === 'decomposing' || 
      (progressData.metadata?.isComplexQuery === true) ||
      (progressData.step && progressData.totalSteps && progressData.totalSteps > 1) ||
      // If a previous stage was 'decomposing', keep the complex flag
      (progressData.sessionId && 
       this.sessionProgress.has(progressData.sessionId) &&
       this.sessionProgress.get(progressData.sessionId).some(p => p.stage === 'decomposing'))
    );
  }
  
  /**
   * Update stored progress for a session
   * @param {string} sessionId The session ID
   * @param {Object} progressData The progress data
   * @private
   */
  _updateSessionProgress(sessionId, progressData) {
    if (!sessionId) return;
    
    if (!this.sessionProgress.has(sessionId)) {
      this.sessionProgress.set(sessionId, []);
    }
    
    // Add to history, limit to 100 entries to prevent memory issues
    const sessionHistory = this.sessionProgress.get(sessionId);
    sessionHistory.push(progressData);
    
    if (sessionHistory.length > 100) {
      sessionHistory.shift(); // Remove oldest
    }
  }
  
  /**
   * Notify all registered listeners for a session
   * @param {string} sessionId The session ID
   * @param {Object} progressData The progress data
   * @private
   */
  _notifyListeners(sessionId, progressData) {
    if (!sessionId) return;
    
    const listeners = this.progressListeners.get(sessionId) || [];
    listeners.forEach(listener => {
      try {
        listener(progressData);
      } catch (error) {
        console.error(`Error in progress listener for session ${sessionId}:`, error);
      }
    });
  }
  
  /**
   * Register a listener for progress updates for a specific session
   * @param {string} sessionId The session ID to track
   * @param {Function} callback The function to call with progress updates
   * @returns {Function} Cleanup function to remove the listener
   * @azure Follows Azure best practice for clean resource management
   */
  trackProgress(sessionId, callback) {
    if (!sessionId) {
      console.warn('Cannot track progress for undefined sessionId');
      return () => {};
    }
    
    if (!this.initialized) {
      this.initialize();
    }
    
    if (!this.progressListeners.has(sessionId)) {
      this.progressListeners.set(sessionId, []);
    }
    
    this.progressListeners.get(sessionId).push(callback);
    
    // Set as active session
    this.activeSession = sessionId;
    
    // If we have any stored progress for this session, send it immediately
    const sessionHistory = this.sessionProgress.get(sessionId) || [];
    if (sessionHistory.length > 0) {
      const latestProgress = sessionHistory[sessionHistory.length - 1];
      setTimeout(() => {
        try {
          callback(latestProgress);
        } catch (error) {
          console.error(`Error in initial progress callback for session ${sessionId}:`, error);
        }
      }, 0);
    }
    
    // Return cleanup function
    return () => {
      const listeners = this.progressListeners.get(sessionId) || [];
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
      
      // If no more listeners for this session, clean up
      if (listeners.length === 0) {
        this.progressListeners.delete(sessionId);
        
        // If this was the active session, clear it
        if (this.activeSession === sessionId) {
          this.activeSession = null;
        }
      }
    };
  }
  
  /**
   * Get the latest progress for a specific session
   * @param {string} sessionId The session ID
   * @returns {Object|null} The latest progress data or null
   */
  getLatestProgress(sessionId) {
    if (!sessionId || !this.sessionProgress.has(sessionId)) {
      return null;
    }
    
    const sessionHistory = this.sessionProgress.get(sessionId);
    if (!sessionHistory || sessionHistory.length === 0) {
      return null;
    }
    
    return sessionHistory[sessionHistory.length - 1];
  }
  
  /**
   * Get the progress history for a session
   * @param {string} sessionId The session ID
   * @returns {Array} Progress history
   */
  getSessionProgressHistory(sessionId) {
    return this.sessionProgress.get(sessionId) || [];
  }
  
  /**
   * Check if a session requires user input
   * @param {string} sessionId The session ID
   * @returns {boolean} True if user input is required
   */
  sessionRequiresUserInput(sessionId) {
    const latestProgress = this.getLatestProgress(sessionId);
    return latestProgress?.stage === 'waiting_for_user_input';
  }
  
  /**
   * Clean up and remove all listeners
   * @azure Follows Azure best practice for proper resource cleanup
   */
  dispose() {
    if (this.cleanup) {
      try {
        this.cleanup();
      } catch (error) {
        console.error('Error during QueryProgressService cleanup:', error);
      }
    }
    
    this.progressListeners.clear();
    this.sessionProgress.clear();
    this.initialized = false;
    this.activeSession = null;
  }
}

// Export a singleton instance
export const queryProgressService = new QueryProgressService();